/** Number and time formatting for the menus (docs/architecture.md §11: numbers use Intl). */
import i18n, { type TFunction } from 'i18next';

/** "13W · 4L" (plus draws when there are any). */
export function recordLabel(t: TFunction, r: { wins: number; losses: number; draws: number }): string {
  return r.draws
    ? t('screens.ui.recordDraws', { w: r.wins, l: r.losses, d: r.draws })
    : t('screens.ui.record', { w: r.wins, l: r.losses });
}

function locale(): string {
  const l = i18n.language;
  return !l || l === 'pseudo' ? 'en' : l;
}

/**
 * Chrome ships no number formats for some of the 39 languages and would fall back to English
 * ("1.5%"); a language with the same decimal comma stands in (numbers only: a date in a borrowed
 * language would be wrong, English is better there).
 */
const NUMBERS_LIKE: Record<string, string> = { is: 'da', mk: 'bg', sq: 'it', be: 'ru', eu: 'es', gl: 'es' };

function numberLocale(): string {
  const l = locale();
  const base = l.split('-')[0]!;
  if (NUMBERS_LIKE[base] && !Intl.NumberFormat.supportedLocalesOf([l]).length) return NUMBERS_LIKE[base];
  return l;
}

const numberFormats = new Map<string, Intl.NumberFormat>();

function numberFormat(options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const loc = numberLocale();
  const key = `${loc}|${JSON.stringify(options)}`;
  let f = numberFormats.get(key);
  if (!f) {
    try {
      f = new Intl.NumberFormat(loc, options);
    } catch {
      f = new Intl.NumberFormat('en', options);
    }
    numberFormats.set(key, f);
  }
  return f;
}

/** Typographic minus, as the designs use. */
const minus = (s: string) => s.replace('-', '−');

export function formatNumber(n: number): string {
  return numberFormat({ maximumFractionDigits: 1 }).format(n);
}

/** Up to two decimals in the current language: 0.08 → "0.08", "0,08" in German. */
export function formatDecimal(n: number): string {
  return numberFormat({ maximumFractionDigits: 2 }).format(n);
}

/** 0.015 → "1.5%" in the current language ("1,5 %" in German, "%1,5" in Turkish); `signed` adds "+". */
export function formatPercent(fraction: number, signed = false): string {
  return minus(numberFormat({ style: 'percent', maximumFractionDigits: 1, signDisplay: signed ? 'always' : 'auto' }).format(fraction));
}

/** 6 → "+6". */
export function formatSigned(n: number): string {
  return minus(numberFormat({ maximumFractionDigits: 1, signDisplay: 'always' }).format(n));
}

/** "Sep 2026" in the current language. */
export function monthYear(ms: number): string {
  try {
    return new Intl.DateTimeFormat(locale(), { month: 'short', year: 'numeric' }).format(ms);
  } catch {
    return new Intl.DateTimeFormat('en', { month: 'short', year: 'numeric' }).format(ms);
  }
}

/** One decimal at most, without a trailing ".0" (1.5, 12, 0.3). */
export function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

/** 41.3 → "0:41" (seconds into a round). */
export function clock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** 7 → "00:07" (matchmaking timer). */
export function clock2(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

export interface Countdown {
  days: number;
  hours: number;
  minutes: number;
}

export function countdown(ms: number): Countdown {
  const t = Math.max(0, ms);
  return {
    days: Math.floor(t / 86_400_000),
    hours: Math.floor((t % 86_400_000) / 3_600_000),
    minutes: Math.floor((t % 3_600_000) / 60_000),
  };
}
